import React, { useState, useEffect } from "react";
import { ArrowDown, ArrowUp, ChevronUp, ChevronDown } from "lucide-react";
import "./StockMovements.scss";
import classificarMovimento from "./classificarMovimento";

/* O título vem de fora: com `auth.userId` o cartão mostra só as movimentações
 * de quem está a olhar, e sem o dizer um cartão vazio parecia avaria em vez de
 * "ainda não mexeste no stock". */
const StockMovements = ({
  clinicaId,
  limit = 5,
  auth,
  sendRequest,
  titulo = "Últimas Movimentações",
}) => {
  const [movements, setMovements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedItems, setExpandedItems] = useState({});

  useEffect(() => {
    const fetchMovements = async () => {
      try {
        setLoading(true);
        const link = auth.userId
          ? `api/stocks/movements/${clinicaId}/user/${auth.userId}?limit=${limit}`
          : `api/stocks/movements/${clinicaId}/user/todos?limit=${limit}`;

        const responseData = await sendRequest(
          `${process.env.REACT_APP_BACKEND_LINK}/${link}`,
          "GET",
          null,
          {
            Authorization: "Bearer " + auth.token,
          }
        );

        setMovements(responseData.movimentos || []);
      } catch (err) {
        setError("Erro ao conectar com o servidor");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    if (clinicaId) {
      fetchMovements();
    }
  }, [clinicaId, limit, sendRequest, auth]);

  const toggleItemExpand = (id) => {
    setExpandedItems((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return (
      date.toLocaleDateString("pt-BR") +
      " " +
      date.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })
    );
  };

  if (loading)
    return (
      <div className="stock-movements-loading">Carregando movimentações...</div>
    );
  if (error) return <div className="stock-movements-error">{error}</div>;

  return (
    <div className="stock-movements-container caixa">
      <div className="stock-movements-header">
        <h2>{titulo}</h2>
        <span className="movement-count">
          Últimos {movements.length} registros
        </span>
      </div>

      <div className="movements-list">
        {movements.length === 0 ? (
          <div className="no-movements">Nenhuma movimentação encontrada</div>
        ) : (
          movements.map((movement) => {
            const { rotulo, saida } = classificarMovimento(movement);
            return (
            <div className="movement-item" key={movement.id || movement._id}>
              <div
                className={`movement-header ${saida ? "outgoing" : "incoming"}`}
                onClick={() => toggleItemExpand(movement.id || movement._id)}
              >
                <div className="movement-type-indicator">
                  {saida ? <ArrowUp size={16} /> : <ArrowDown size={16} />}
                </div>

                <div className="movement-summary">
                  <div className="movement-title">
                    <span
                      className={`movement-direction ${
                        saida ? "outgoing" : "incoming"
                      }`}
                    >
                      {rotulo}
                    </span>
                    <span className="movement-quantity">
                      - {movement.quantidade}{" "}
                      {movement.produto?.nome || "Produto"}
                    </span>
                    <span
                      className={`status-tag ${
                        movement.loteVazio ? "empty" : "full"
                      }`}
                    >
                      <span className="detail-value">
                        {movement.responsavelMovimento?.responsavel?.name ||
                          "N/A"}
                      </span>
                    </span>
                  </div>

                  <div className="movement-details">
                    <span className="movement-product">
                      {movement.produto?.categoriaStock?.nome || "Categoria"} |{" "}
                      {movement.descarteId ? "Descarte" : "Movimentação Padrão"}
                    </span>
                  </div>
                </div>

                <div className="movement-expand">
                  {expandedItems[movement.id || movement._id] ? (
                    <ChevronUp size={16} />
                  ) : (
                    <ChevronDown size={16} />
                  )}
                </div>
              </div>

              {expandedItems[movement.id || movement._id] && (
                <div className="movement-expanded-details">
                  <div className="detail-row">
                    <span className="detail-label">Lote:</span>
                    <span className="detail-value">
                      {movement.lote || "N/A"}
                    </span>
                    <span className="detail-date">
                      {formatDate(movement.data)}
                    </span>
                  </div>

                  {/* O bloco que aqui estava dependia do `movement.saida`
                      inexistente, por isso nunca aparecia — e repetia as
                      observações que o bloco de descarte já mostra a seguir. */}
                  {movement.descarteId && (
                    <div className="discard-info">
                      <span className="discard-label">Descarte:</span>
                      <span className="discard-reason">
                        {movement.descarteId.motivo}
                      </span>
                      {movement.descarteId.observacoes && (
                        <span className="discard-notes">
                          {movement.descarteId.observacoes}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default StockMovements;
